import * as draftStorage from "@/components/session/sessionDraftStorage";
import { generateId } from "@/utils";
import { dateToUnixTimestamp } from "@/utils/dates";

import LinkService, { linkRefsFromIdToAlmType } from "./linkService";
import SessionHandler from "./sessionHandler";

const settingsKeys = [
  "confirm_sticky_delete",
  "team_forbid_sticky_delete",
  "move_activated",
  "backlog_forbid_JIRA_sticky_delete",
  "backlog_forbid_WSJF_edit",
  "backlog_synchronize_ADO_iteration",
  "backlog_mirror_assigned_stickies_to_team_board",
  "backlog_mirror_assigned_stickies_to_program_board_alm",
  "iteration_load_warn",
  "iteration_load_critical",
  "backlog_synchronize_rally_teams",
  "backlog_synchronize_ADO_team",
  "auto_dependency_bridge",
  "show_dev_links",
];

export class SessionDuplicationService {
  constructor(sessionId, sessionInfo, almHandler, context) {
    this.sourceSessionId = sessionId;
    this.sessionHandler = new SessionHandler(context, sessionId);
    this.sessionInfo = sessionInfo;
    this.almHandler = almHandler;
    this.context = context;
  }

  async duplicateSession(withIterations) {
    // also drops the selected teams, ARTs and solution train: the user picks them
    // fresh, as the org structure may have changed since the source session
    draftStorage.clearDraft();
    const linkService = new LinkService(this.sessionHandler, this.almHandler);
    const oldIterations = await this.sessionHandler.loadIterations();
    const iterationDuration =
      dateToUnixTimestamp(oldIterations[0].end) - dateToUnixTimestamp(oldIterations[0].start);
    const iterations = oldIterations.map((iteration) => ({
      ...iteration,
      start: withIterations ? dateToUnixTimestamp(iteration.start) : null,
      end: withIterations ? dateToUnixTimestamp(iteration.end) : null,
    }));
    const { start, end } = await this.sessionHandler.getPlanningDates();
    draftStorage.setGeneralInformation({
      piPlanningName: "Duplicate of " + (await this.sessionHandler.getName()),
      planningStartDate: !start || start.getTime() < Date.now() ? undefined : start,
      planningEndDate: !end || end.getTime() < Date.now() ? undefined : end,
      noOfIterations: iterations.length,
      durationOfOneIterationInDays: 1 + Math.round(iterationDuration / (24 * 60 * 60)),
      almTool: this.sessionInfo.alm_connection_type || "",
      selectedConnectionID: this.sessionInfo.alm_connection_id,
      previouslySelectedAlmTool: this.sessionInfo.alm_connection_id,
      sync_session_id: this.sessionInfo.sync_session_id,
      almToolName: this.almHandler.isAlm ? await this.almHandler.getName() : "None",
    });

    const rawSettings = await this.sessionHandler.getSettings();
    const duplicatedSettings = Object.fromEntries(
      settingsKeys.map((key) => [key, rawSettings[key]]),
    );
    // Carry the ADO team-sync toggle as an explicit boolean so the duplicate
    // mirrors its source: a source predating the feature has no value here, and
    // we must NOT let the duplicate inherit the new-session default-on.
    duplicatedSettings.backlog_synchronize_ADO_team =
      rawSettings.backlog_synchronize_ADO_team ?? false;
    draftStorage.setSettings(duplicatedSettings);

    const teams = await this.sessionHandler.getTeams();
    teams.forEach((team) => (team.id = team.user_id));

    const teamStickies = await this.loadBoardStickies("team");
    const riskStickies = await this.loadBoardStickies("risk");
    const backlogStickies = await this.loadBoardStickies("backlog");
    const programStickies = await this.loadBoardStickies("program");
    const solutionStickies = await this.loadBoardStickies("solution");
    const solution_backlogStickies = await this.loadBoardStickies("solution_backlog");
    const stickies = [
      ...teamStickies,
      ...riskStickies,
      ...backlogStickies,
      ...programStickies,
      ...solutionStickies,
      ...solution_backlogStickies,
    ];

    draftStorage.setTeamStickies(teamStickies);
    draftStorage.setRiskStickies(riskStickies);
    draftStorage.setBacklogStickies(backlogStickies);
    draftStorage.setProgramStickies(programStickies);
    draftStorage.setSolutionPlanningStickies(solutionStickies);
    draftStorage.setSolutionBacklogStickies(solution_backlogStickies);

    const stickerTypes = await this.sessionHandler.getStickerTypes();
    if (stickerTypes && stickerTypes.length > 0) {
      draftStorage.setStickers(
        stickerTypes.map((s) => ({ icon: s.icon, color: s.color, label: s.label })),
      );
    }

    const flexBoards = await this.sessionHandler.loadFlexBoards();
    flexBoards.forEach((f) => {
      delete f.id;
      (f.stickyTypes || []).forEach((s) => delete s.id);
    });
    draftStorage.setFlexBoards(flexBoards);

    // Set team field mapping + reference Kanban board (one-art jira only)
    let teamFieldMapping = {};
    let sessionKanbanBoardId = null;
    if (this.sessionInfo.alm_connection_type === "jira") {
      const oldArts = await this.sessionHandler.getArts();
      if (oldArts.length === 1) {
        teamFieldMapping = (await this.almHandler.getTeamFieldMappingForOneArtSession()).data;
        sessionKanbanBoardId = (await this.almHandler.getSessionKanbanBoard(this.sourceSessionId))
          .board_id;
      }
    }
    draftStorage.setTeamFieldMapping(teamFieldMapping);
    draftStorage.setSessionKanbanBoard(sessionKanbanBoardId ?? null);

    if (this.almHandler.isAlm) {
      if (this.almHandler.supportsLinks) {
        const teams = (await this.sessionHandler.getTeams()).map(({ user_id, art_id }) => ({
          id: user_id,
          artId: art_id,
        }));
        const linkStickyTypes = await linkService.createLinkStickyTypes(
          await linkService.getStickyTypesWithAlmInfo(),
          await this.almHandler.getIssueTypes(),
          teams,
          await this.almHandler.getProjects(),
        );
        const issueLinkTypes = await this.almHandler.getIssueLinkTypes();
        const links = await linkService.getLinks(linkStickyTypes, issueLinkTypes);
        links.forEach((l) => delete l.id);
        linkRefsFromIdToAlmType(links, stickies);
        draftStorage.setLinks(links);
      }

      await this.almHandler.addTeamsToIterations(iterations, teams, true);
      if (!withIterations) {
        iterations.forEach((iter) => {
          iter.teams.forEach((team) => {
            team.sources.forEach((source) => {
              source.sprint = null;
            });
          });
        });
      }
    }
    draftStorage.setIterations(iterations);
  }

  async loadBoardStickies(boardType) {
    const stickyTypes = await this.sessionHandler.loadStickyTypes(boardType, this.almHandler);
    return stickyTypes
      .filter((sticky) => sticky.origin_board_type === boardType)
      .map((sticky) => ({
        ...sticky,
        id: null,
        _id: sticky.id,
        frontend_id: sticky.alm_type || generateId(),
      }));
  }
}
